import get from 'lodash/get'
import set from 'lodash/set'
import split from 'lodash/split'
import parseInt from 'lodash/parseInt'
import isBoolean from 'lodash/isBoolean'
import { Map, List } from 'immutable'
import React, { Component } from 'react'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from 'actions'
import FormsyForm from 'formsy-react'
import PropTypes from 'prop-types'
import { TextInput } from 'components/FormInput.v2'
import SocialButton, { SOCIAL_BUTTON_TYPES, SOCIAL_BUTTON_SIZES } from 'components/SocialButton'
import { getShareV3Link, openWindow, SHARE_LINKS } from 'services/share'
import Sherpa, { TYPE_LARGE } from 'components/Sherpa'
import { TARGET_SELF } from 'components/Link'
import Button, { TYPES } from 'components/Button'
import { SHARE_COMPLETED_GA4_EVENT } from 'services/event-tracking'
import { TYPE_CONTENT_VIDEO } from 'services/content-type'
import { getPrimary } from 'services/languages'
import { get as getConfig } from 'config'
import { getPathLocationName } from 'services/url'
import ShareInterstitial from '../Interstitial'
import ShareEmbed from '../Embed'

const isShareV3Enabled = !!getConfig().features.sharePageV3

function format (time) {
  if (time < 10) {
    return `0${time}`
  }
  return `${time}`
}

function getPlayerTimecode () {
  const { videojs } = global
  if (videojs && videojs.getAllPlayers) {
    const players = videojs.getAllPlayers()
    const player = get(players, 0, undefined)
    try {
      return player ? player.currentTime() : 0
    } catch (e) {
      // Player threw an error just return zero
      return 0
    }
  }
  return 0
}

function formatTimecode (time) {
  const flooredTime = Math.floor(time)
  const seconds = flooredTime % 60
  let minutes = Math.floor(flooredTime / 60)
  const hours = Math.floor(minutes / 60)
  minutes -= hours * 60
  return `${format(hours)}:${format(minutes)}:${format(seconds)}`
}

export function getTimecode (timecode) {
  const parts = split(timecode, ':')
  const hours = parts.length === 3
    ? parseInt(parts[0]) * 3600
    : 0
  const minutes = parts.length >= 2
    ? parseInt(parts[1]) * 60
    : 0
  const seconds = parts.length >= 1
    ? parseInt(parts[2])
    : 0
  return hours + minutes + seconds
}

function getSource (type) {
  switch (type) {
    case SHARE_LINKS.FACEBOOK:
      return 'facebook'
    case SHARE_LINKS.REDDIT:
      return 'reddit'
    case SHARE_LINKS.TWITTER:
      return 'twitter'
    default:
      return 'copy'
  }
}

function getShareLink (props, state) {
  const { token, userLang } = props
  const { timecode, openEmbed, includeTimecode } = state
  if (token) {
    const langQuery = `?language[]=${userLang}`
    const timecodeQuery = includeTimecode ? `&startAt=${timecode}` : ''
    const embedQuery = openEmbed ? '&embed=true' : ''
    const queryString = `${langQuery}${timecodeQuery}${embedQuery}&utm_source=share`
    return `${global.location.origin}/share/${token}${queryString}`
  }
  return ''
}

class ModalShare extends Component {
  //
  static getDerivedStateFromProps (props, state) {
    const { interstitialConfirmed } = state
    const { hasShared, userRfd } = props
    // always update the shareLink
    // if nothing changed, it will not cause a re-render
    const shareLink = isShareV3Enabled
      ? `${global.location.origin}${getShareV3Link(props)}`
      : getShareLink(props, state)
    const nextState = { shareLink }
    // determine if we should open the interstitial page
    // that explains a first-time share to users.
    if (isShareV3Enabled && userRfd) {
      set(nextState, 'view', 'share')
    } else if (isBoolean(hasShared) && !interstitialConfirmed) {
      set(nextState, 'view', hasShared ? 'share' : 'interstitial')
      set(nextState, 'interstitialConfirmed', hasShared)
      set(nextState, 'openInterstitial', !hasShared)
    }
    return nextState
  }

  constructor (props) {
    //
    super(props)
    // populate with initial user
    // and video player data
    const { staticText } = props
    const timecode = getPlayerTimecode()

    this.timecodeValidations = {
      matchRegexp: /^(\d|:)+$/,
    }

    this.timecodeErrors = {
      matchRegexp: staticText.getIn(['data', 'invalidTimecode']),
    }

    this.state = {
      interstitialConfirmed: !!isShareV3Enabled,
      initialTimecode: timecode,
      includeTimecode: false,
      view: 'loading',
      shareLink: '',
      siteUrl: '',
      timecode,
    }
  }

  componentDidMount () {
    const { props, state } = this
    const { getUserReferralData, getUserHasShared } = props
    if (isShareV3Enabled) {
      getUserReferralData()
    } else {
      getUserHasShared()
      this.createShare(state)
    }
  }

  componentDidUpdate (_prevProps, prevState) {
    const { props, state } = this
    const { token, view, update } = props

    const { siteUrl } = state

    const { view: prevView } = prevState

    if (view !== prevView) {
      update()
    }

    if (siteUrl && token) {
      this.connectMedia()
    }
  }

  onBlurTimecode = (value) => {
    const { props, state } = this
    const { initialTimecode } = state
    const { duration } = props
    const time = getTimecode(value)

    if (time < duration) {
      return formatTimecode(time)
    }
    return formatTimecode(initialTimecode)
  }

  onChecked = (key, checked) => {
    return key === 'timecode' ? this.setState(() => ({
      includeTimecode: checked,
    })) : null
  }

  onChange = (value, { name, isValidValue }) => {
    if (isValidValue(value)) {
      this.setState(() => ({
        [name]: name === 'timecode'
          ? getTimecode(value)
          : value,
      }))
    }
    return value
  }

  openEmbed = () => {
    const { state } = this
    this.createShare(state)
    this.setState(() => ({
      openEmbed: true,
      view: 'embed',
    }))
  }

  connectMedia () {
    const { state, props } = this
    const { setShareSource, token } = props
    const { siteUrl, shareLink } = state

    const source = getSource(siteUrl)
    const shareUrl = encodeURIComponent(shareLink)

    setShareSource({ token, source })

    const url = siteUrl.replace('{url}', shareUrl)
      .replace('{user_id}', SHARE_LINKS.GAIA_USER)
      .replace('{title}', this.props.title)

    if (siteUrl.includes('mailto')) {
      window.location.href = url
    } else {
      openWindow(null, true, url)
    }
    this.setState(() => ({
      siteUrl: '',
    }))
  }

  confirmInterstitial = () => {
    this.setState(() => ({
      interstitialConfirmed: true,
      openInterstitial: false,
      view: 'share',
    }))
  }

  confirmEmbed = () => {
    this.setState(() => ({
      openEmbed: false,
      view: 'share',
    }))
  }

  createShare = (data) => {
    const { props } = this
    const { token } = props

    if (!token && !isShareV3Enabled) {
      const { siteUrl } = data
      const source = getSource(siteUrl)
      const { contentType, contentId, createShare } = props
      createShare({
        personalization: null,
        contentId,
        source,
        type: contentType,
      })
    }
  }

  shareMedia (siteUrl) {
    return (e) => {
      e.preventDefault()
      const { state } = this
      this.createShare(state)
      this.setState(() => ({
        siteUrl,
      }))
    }
  }

  sendGenerateLinkCTAEvent = () => {
    const { props } = this
    const {
      path,
      title,
      contentId,
      uiLocation,
      contentType,
      parentSeriesId,
      parentSeriesName,
      setDefaultGa4Event,
    } = props

    // GA4 event
    let ga4ShareCompletedEventData = SHARE_COMPLETED_GA4_EVENT.mergeDeep({
      content_id: contentId,
      content_name: title,
      content_type: contentType || TYPE_CONTENT_VIDEO,
      ui_location: uiLocation || getPathLocationName(path),
    })

    // Non series videos
    if (parentSeriesName) {
      ga4ShareCompletedEventData = ga4ShareCompletedEventData
        .mergeDeep({ parent_series_name: parentSeriesName })
    }

    if (parentSeriesId) {
      ga4ShareCompletedEventData = ga4ShareCompletedEventData
        .mergeDeep({ parent_series_id: parentSeriesId })
    }

    setDefaultGa4Event(ga4ShareCompletedEventData)
  }

  render () {
    const {
      props,
      state,
      confirmInterstitial,
      confirmEmbed,
    } = this
    const {
      view, shareLink, initialTimecode,
    } = state
    const {
      token, staticText, enableTime, duration,
    } = props

    if (view === 'loading') {
      return <Sherpa type={TYPE_LARGE} />
    }

    if (view === 'interstitial') {
      return (
        <ShareInterstitial
          onContinue={confirmInterstitial}
          staticText={staticText}
        />
      )
    }

    if (view === 'embed') {
      return (
        <ShareEmbed
          onContinue={confirmEmbed}
          staticText={staticText}
          shareLink={shareLink}
        />
      )
    }

    return (
      <FormsyForm
        className="modal-share__form"
        onSubmit={this.createShare}
      >
        <div className="modal-share__description">
          {staticText.getIn(['data', 'shareUnlimited'])}
        </div>
        <div className="modal-share__input-wrapper">
          <div className="modal-share__options">
            {enableTime && !isShareV3Enabled
              ? <div className="modal-share__option modal-share__option-time">
                <TextInput
                  label={staticText.getIn(['data', 'startAt'])}
                  value={formatTimecode(initialTimecode) || ''}
                  validations={this.timecodeValidations}
                  onBlur={this.onBlurTimecode}
                  onChecked={this.onChecked}
                  onChange={this.onChange}
                  duration={duration}
                  autocomplete="off"
                  name="timecode"
                  checkable
                  block
                />
              </div>
              : null}
          </div>
        </div>
        <div className="modal-share__input-wrapper">
          <TextInput
            label={staticText.getIn(['data', 'link'])}
            value={shareLink || ''}
            copyable={!!shareLink}
            autocomplete="off"
            name="link"
            readonly
            block
          />
          {token || isShareV3Enabled
            ? null
            : (
              <Button
                type={TYPES.GHOST}
                submit
                className="modal-share__link-btn"
                onClick={this.sendGenerateLinkCTAEvent}
              >
                {staticText.getIn(['data', 'generateLink'])}
              </Button>
            )
          }
        </div>
        <div className="modal-share__social">
          {isShareV3Enabled
            ? null
            : <ul className="modal-share__btns modal-share__btns__left">
              <li>
                <SocialButton
                  size={SOCIAL_BUTTON_SIZES.LARGE}
                  type={SOCIAL_BUTTON_TYPES.EMBED}
                  onClick={this.openEmbed}
                  target={TARGET_SELF}
                />
              </li>
              <li className="modal-share__btns__vertical" />
            </ul>
          }
          <ul className="modal-share__btns modal-share__btns__right">
            <li>
              <SocialButton
                size={SOCIAL_BUTTON_SIZES.LARGE}
                type={SOCIAL_BUTTON_TYPES.TWITTER}
                onClick={this.shareMedia(SHARE_LINKS.TWITTER)}
              />
            </li>
            <li>
              <SocialButton
                size={SOCIAL_BUTTON_SIZES.LARGE}
                type={SOCIAL_BUTTON_TYPES.FACEBOOK}
                onClick={this.shareMedia(SHARE_LINKS.FACEBOOK)}
              />
            </li>
            <li>
              <SocialButton
                size={SOCIAL_BUTTON_SIZES.LARGE}
                type={SOCIAL_BUTTON_TYPES.REDDIT}
                onClick={this.shareMedia(SHARE_LINKS.REDDIT)}
              />
            </li>
          </ul>
        </div>
      </FormsyForm>
    )
  }
}

ModalShare.propTypes = {
  contentUrl: PropTypes.string.isRequired,
  contentId: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  contentType: PropTypes.string.isRequired,
  parentSeriesId: PropTypes.number,
  parentSeriesName: PropTypes.string,
  uiLocation: PropTypes.string,
  enableTime: PropTypes.bool,
}

ModalShare.defaultProps = {
  contentType: TYPE_CONTENT_VIDEO,
}

export default connectRedux(
  (state) => ({
    auth: state.auth,
    userRfd: state.userReferrals.getIn(['data', 'referralId'], ''),
    hasShared: state.share.getIn(['hasShared', 'data', 'hasShared']),
    duration: state.video.getIn(['data', 'feature', 'duration'], 0),
    staticText: state.staticText.getIn(['data', 'share'], Map()),
    token: state.share.getIn(['created', 'data', 'token'], ''),
    userLang: getPrimary(state.user.getIn(['data', 'language'], List())),
    userUid: state.user.getIn(['data', 'uuid']),
    userAffId: state.ambassadors.getIn(['data', 'everflowAffiliateId']),
    path: state.resolver.get('path'),
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      getUserReferralData: actions.userReferrals.getUserReferralData,
      getUserHasShared: actions.share.getUserHasShared,
      setShareSource: actions.share.setShareSource,
      dismissModal: actions.dialog.dismissModal,
      createShare: actions.share.createShare,
      clearShare: actions.share.clearShare,
      setDefaultGa4Event: actions.eventTracking.setDefaultGa4Event,
    }
  },
)(ModalShare)
